/* eslint-disable no-param-reassign */
import CONSTANTS from '@picsio/db/src/constants';
import { createAsyncThunk } from '@reduxjs/toolkit';

import localization from '../../shared/strings';

import { added } from '../keywords/actions';
import { add } from '../keywordsList/actions';
import { selectProductSelectionPayload } from '../selectors/productSelectionSelectors';

import { fetchAggregatedProductDetails } from './fetchAggregatedProductDetails';
import {
  getAggregatedSavingKey,
  splitMasterSelectionItems,
} from './helpers/buildBulkMasterFieldUpdate';
import { isMasterSelectionContext } from './helpers/selectionContext';

const aggregatedL10n = localization.PRODUCT_INFO.aggregated;
const KEYWORDS_SAVING_KEY = getAggregatedSavingKey({ fieldKey: 'keywords' });

const resolveMasterKeywordProductIds = (state) => {
  const { context } = state.products.ui.selection;

  if (!isMasterSelectionContext(context)) {
    return { ignored: true, reason: 'notMasterContext' };
  }

  const items = selectProductSelectionPayload(state);
  const { productIds } = splitMasterSelectionItems(items);

  if (!productIds.length) {
    return { ignored: true, reason: 'noApplicableIds' };
  }

  return { productIds };
};

const getKeywordMutationTargets = (getState, Toast) => {
  const resolved = resolveMasterKeywordProductIds(getState());
  if (resolved.ignored && resolved.reason === 'noApplicableIds') {
    Toast(aggregatedL10n.updateFailed, { type: 'error', autoClose: 5000 });
  }
  return resolved;
};

const scheduleSilentKeywordRefetch = (dispatch, getState, requestId) => {
  const run = () => {
    const lastAppliedId = getState().products.ui.aggregatedDetails
      .lastAppliedByKey?.[KEYWORDS_SAVING_KEY];
    if (lastAppliedId !== requestId) return;

    dispatch(fetchAggregatedProductDetails({ silent: true })).catch(() => {});
  };

  // RTK awaits this payload, then dispatches fulfilled. Refetch after that.
  Promise.resolve()
    .then(() => Promise.resolve())
    .then(() => Promise.resolve())
    .then(run)
    .catch(() => {});
};

const syncKeywordCatalog = (dispatch, keywords = []) => {
  keywords.forEach((keyword) => {
    const clonedKeyword = { ...keyword };

    if (clonedKeyword.isNew) {
      clonedKeyword.isFavorite = false;
      clonedKeyword.count = 0;
      dispatch(added([clonedKeyword]));
    } else {
      dispatch(add([{
        ...clonedKeyword,
        name: clonedKeyword.name
          || clonedKeyword.path.split(CONSTANTS.NEW_PATH_DELIMITER).pop(),
      }]));
    }
  });
};

export const attachAggregatedKeyword = createAsyncThunk(
  'products/attachAggregatedKeyword',
  async ({ keywordName }, {
    getState,
    dispatch,
    requestId,
    extra: {
      sdk, Logger, Toast, utils, showErrorDialog,
    },
  }) => {
    const resolved = getKeywordMutationTargets(getState, Toast);
    if (resolved.ignored) return resolved;

    const { productIds } = resolved;

    try {
      const { data: keywords } = await sdk.products.addKeywords(productIds, keywordName);
      syncKeywordCatalog(dispatch, keywords);
      scheduleSilentKeywordRefetch(dispatch, getState, requestId);

      return {
        ignored: false,
        productIds,
        keywordName,
        keywords,
      };
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg')
        || aggregatedL10n.updateFailed;
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error bulk add keyword to products'), { error }, [
        'BulkAddKeywordToProductFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const detachAggregatedKeyword = createAsyncThunk(
  'products/detachAggregatedKeyword',
  async ({ keywordIds }, {
    getState,
    dispatch,
    requestId,
    extra: {
      sdk, Logger, Toast, utils, showErrorDialog,
    },
  }) => {
    const resolved = getKeywordMutationTargets(getState, Toast);
    if (resolved.ignored) return resolved;

    const { productIds } = resolved;

    try {
      await sdk.products.removeKeywords(productIds, keywordIds);
      scheduleSilentKeywordRefetch(dispatch, getState, requestId);

      return {
        ignored: false,
        productIds,
        keywordIds,
      };
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg')
        || aggregatedL10n.updateFailed;
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error bulk remove keywords from products'), { error }, [
        'BulkRemoveKeywordFromProductFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

const markSaving = (state, meta) => {
  state.ui.aggregatedDetails.savingByKey = {
    ...state.ui.aggregatedDetails.savingByKey,
    [KEYWORDS_SAVING_KEY]: meta.requestId,
  };
};

const clearSavingIfLatest = (state, meta) => {
  if (state.ui.aggregatedDetails.savingByKey?.[KEYWORDS_SAVING_KEY] !== meta.requestId) {
    return false;
  }

  const nextSaving = { ...state.ui.aggregatedDetails.savingByKey };
  delete nextSaving[KEYWORDS_SAVING_KEY];
  state.ui.aggregatedDetails.savingByKey = nextSaving;
  return true;
};

const idsIn = (ids, productId) => (
  (ids || []).some((id) => String(id) === String(productId))
);

const mergeKeywords = (current, incoming) => {
  const existing = current || [];
  const next = existing.filter(
    (keyword) => !incoming.find(({ _id }) => String(_id) === String(keyword._id)),
  );
  return [...next, ...incoming];
};

const applyKeywordPatchToList = (list, payload) => {
  if (!list?.length) return;

  list.forEach((product) => {
    if (!idsIn(payload.productIds, product._id)) return;

    if (payload.keywords?.length) {
      product.keywords = mergeKeywords(product.keywords, payload.keywords);
      return;
    }

    if (payload.keywordIds?.length) {
      const remove = new Set(payload.keywordIds.map(String));
      product.keywords = (product.keywords || []).filter(
        (keyword) => !remove.has(String(keyword._id)),
      );
    }
  });
};

const applyKeywordMutationFulfilled = (state, { meta, payload }) => {
  const isLatest = clearSavingIfLatest(state, meta);
  if (!isLatest || payload?.ignored) return;

  state.ui.aggregatedDetails.lastAppliedByKey[KEYWORDS_SAVING_KEY] = meta.requestId;
  applyKeywordPatchToList(state.items, payload);
  applyKeywordPatchToList(state.search?.items, payload);
};

export const reducer = (builder) => {
  builder
    .addCase(attachAggregatedKeyword.pending, (state, { meta }) => {
      markSaving(state, meta);
    })
    .addCase(detachAggregatedKeyword.pending, (state, { meta }) => {
      markSaving(state, meta);
    })
    .addCase(attachAggregatedKeyword.fulfilled, applyKeywordMutationFulfilled)
    .addCase(detachAggregatedKeyword.fulfilled, applyKeywordMutationFulfilled)
    .addCase(attachAggregatedKeyword.rejected, (state, { meta }) => {
      clearSavingIfLatest(state, meta);
    })
    .addCase(detachAggregatedKeyword.rejected, (state, { meta }) => {
      clearSavingIfLatest(state, meta);
    });
};
